import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { MessageCircle, Phone, Mail, Smartphone } from 'lucide-react';
import { useLang } from '@/lib/i18n';
import { lv } from '@/components/MultiLangField';

const defaultNav = [
  { label: { pl: 'Strona glowna', en: 'Home', ru: 'Главная', ua: 'Головна' }, url: '/' },
  { label: { pl: 'Katalog', en: 'Catalog', ru: 'Каталог', ua: 'Каталог' }, url: '/modelki' },
  { label: { pl: 'FAQ', en: 'FAQ', ru: 'FAQ', ua: 'FAQ' }, url: '/faq' },
  { label: { pl: 'Uslugi', en: 'Services', ru: 'Услуги', ua: 'Послуги' }, url: '/uslugi' },
  { label: { pl: 'O nas', en: 'About', ru: 'О нас', ua: 'Про нас' }, url: '/o-nas' },
];

const defaultInfo = [
  { label: { pl: 'Regulamin', en: 'Terms', ru: 'Правила', ua: 'Правила' }, url: '/regulamin' },
  { label: { pl: 'Polityka prywatnosci', en: 'Privacy', ru: 'Конфиденциальность', ua: 'Конфіденційність' }, url: '/polityka-prywatnosci' },
  { label: { pl: 'Praca', en: 'Work', ru: 'Работа', ua: 'Робота' }, url: '/praca' },
  { label: { pl: 'Kontakt', en: 'Contact', ru: 'Контакт', ua: 'Контакт' }, url: '/kontakt' },
];

export default function Footer() {
  const [settings, setSettings] = useState({});
  const { t, lang } = useLang();
  const user = JSON.parse(localStorage.getItem('user_data') || 'null');

  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/settings`)
      .then(r => r.json())
      .then(setSettings)
      .catch(() => {});
  }, []);

  const navLinks = (settings.footerLinksNav && settings.footerLinksNav.length > 0) ? settings.footerLinksNav : defaultNav;
  const infoLinks = (settings.footerLinksInfo && settings.footerLinksInfo.length > 0) ? settings.footerLinksInfo : defaultInfo;

  const navHeading = lv(settings.footerNavHeading, lang) || t('footer.nav');
  const infoHeading = lv(settings.footerInfoHeading, lang) || t('footer.info');

  const renderLink = (link, i) => {
    const label = lv(link.label, lang) || (typeof link.label === 'string' ? link.label : link.label?.pl || '');
    const url = link.url || '/';
    const isExternal = url.startsWith('http');

    if (isExternal) {
      return (
        <a key={i} href={url} target="_blank" rel="noopener noreferrer"
          className="text-white/40 text-sm hover:text-[var(--color-primary)] transition-colors">
          {label}
        </a>
      );
    }
    return (
      <Link key={i} to={url}
        className="text-white/40 text-sm hover:text-[var(--color-primary)] transition-colors">
        {label}
      </Link>
    );
  };

  return (
    <footer data-testid="main-footer" className="modern-footer mt-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          <div>
            <Link to="/" className="brand-lockup brand-lockup-footer mb-5" aria-label={settings.siteName || 'SecretGirls Escort'}>
              <span className="brand-monogram" aria-hidden="true"><span>S</span><span>G</span></span>
              <span className="brand-wordmark"><strong>SecretGirls</strong><small>Escort</small></span>
            </Link>
            <p className="text-white/40 text-sm leading-relaxed max-w-xs">{lv(settings.siteDescription, lang) || ''}</p>
          </div>
          <div>
            <h4 className="font-medium text-white mb-4 text-xs tracking-[0.2em] uppercase">{navHeading}</h4>
            <div className="flex flex-col gap-2">
              {navLinks.map(renderLink)}
            </div>
          </div>
          <div>
            <h4 className="font-medium text-white mb-4 text-xs tracking-[0.2em] uppercase">{infoHeading}</h4>
            <div className="flex flex-col gap-2">
              {infoLinks.map(renderLink)}
              <button
                type="button"
                onClick={() => window.dispatchEvent(new CustomEvent('open-pwa-install'))}
                className="flex items-center gap-1.5 text-white/50 text-sm hover:text-[var(--color-primary)] transition-colors text-left group mt-1"
              >
                <Smartphone className="w-4 h-4 text-rose-500 group-hover:scale-110 transition-transform shrink-0" />
                <span>
                  {lang === 'pl' ? 'Aplikacja mobilna (PWA)' : lang === 'ru' ? 'Мобильное приложение (PWA)' : lang === 'ua' ? 'Мобільний додаток (PWA)' : 'Mobile App (PWA)'}
                </span>
              </button>
            </div>
          </div>
          {user && (
          <div>
            <h4 className="font-medium text-white mb-4 text-xs tracking-[0.2em] uppercase">{t('footer.contactTitle')}</h4>
            <div className="flex flex-col gap-3">
              {(settings.contacts?.email || settings.contactEmail) && (
                <a href={`mailto:${settings.contacts?.email || settings.contactEmail}`} data-testid="footer-email"
                  className="flex items-center gap-2 text-white/40 text-sm hover:text-[var(--color-primary)] transition-colors">
                  <Mail className="w-4 h-4" /> {settings.contacts?.email || settings.contactEmail}
                </a>
              )}
              {settings.contacts?.telegram && (
                <a href={`https://t.me/${settings.contacts.telegram}`} target="_blank" rel="noopener noreferrer"
                  className="flex items-center gap-2 text-white/40 text-sm hover:text-[var(--color-primary)] transition-colors">
                  <MessageCircle className="w-4 h-4" /> Telegram
                </a>
              )}
              {settings.contacts?.whatsapp && (
                <a href={`https://wa.me/${settings.contacts.whatsapp.replace(/\D/g,'')}`} target="_blank" rel="noopener noreferrer"
                  className="flex items-center gap-2 text-white/40 text-sm hover:text-[var(--color-primary)] transition-colors">
                  <Phone className="w-4 h-4" /> WhatsApp
                </a>
              )}
            </div>
          </div>
          )}
        </div>
        <div className="border-t border-white/5 mt-8 pt-8 text-center">
          <p className="text-white/20 text-xs">{lv(settings.footerText, lang) || t('footer.copyright')}</p>
        </div>
      </div>
    </footer>
  );
}
